import { useCallback, useContext, useState } from "react";
import { deleteAddon, postAddon } from "@modules/eirSalesFacade/apis/addons";
import { useOfferAddons } from "@modules/eirSalesFacade/swr/offerAddons";
import {
    filterWifiMeshCartAddonList,
    findWifiMeshOfferAddon,
    mapNewCartAddonIdList,
} from "@features/cart/components/BroadbandCard/BroadbandCartAddon/utils";
import { ICart, ICartAddon } from "@features/cart/ICart";
import { ADDON_WIFI_MESH_CODE } from "@features/cart/components/BroadbandCard/BroadbandCartAddon/constants";
import { useFetchState } from "@hooks/useFetchState";
import { sendAddAddonToCartEvent } from "@features/dataLayer/addToCart";
import { NumberOfferContext } from "../../../../../App";
import { getCartItemsNumber } from "@utils/getCartItemsNumber";
import { sendRemoveAddonEvent } from "@features/dataLayer/removeCart";

interface IParams {
    cartAddonList: ICartAddon[];
    offerId: number;
    setCart(cart: ICart | undefined): void;
}

export const useBroadbandCartAddon = (props: IParams) => {
    const { cartAddonList, offerId, setCart } = props;
    const { startFetching, endFetching, isLoading } = useFetchState();

    const { setNumberOfferSelected } = useContext(NumberOfferContext);

    const { data: offerAddonList } = useOfferAddons(offerId);

    const wifiMeshOfferAddon = findWifiMeshOfferAddon(offerAddonList);
    const wifiMeshCartAddonList = filterWifiMeshCartAddonList(cartAddonList);

    const defaultAddonIdList = wifiMeshCartAddonList ? wifiMeshCartAddonList.flatMap((cartAddon) => cartAddon.id) : [];

    const [addonIdList, setAddonIdList] = useState<number[]>(defaultAddonIdList);

    const counter = addonIdList.length;

    const addAddon = useCallback(async () => {
        try {
            startFetching();
            const newCart = await postAddon(offerId, { catalogCode: ADDON_WIFI_MESH_CODE });
            const newAddonIdList = mapNewCartAddonIdList(newCart);
            setAddonIdList(newAddonIdList);
            setCart(newCart);
            setNumberOfferSelected(getCartItemsNumber(newCart));
            sendAddAddonToCartEvent(newCart, offerId);
            endFetching();
        } catch (e) {
            endFetching(e);
        }
    }, [startFetching, offerId, setCart, setNumberOfferSelected, endFetching]);

    const removeAddon = useCallback(async () => {
        try {
            startFetching();
            const addonToRemove = cartAddonList.findLast((addon) => addon.id === addonIdList[0]);
            if (addonToRemove) {
                const newCart = await deleteAddon(offerId, addonToRemove.id);
                const newAddonIdList = mapNewCartAddonIdList(newCart);
                setAddonIdList(newAddonIdList);
                setCart(newCart);
                setNumberOfferSelected(getCartItemsNumber(newCart));
                sendRemoveAddonEvent(newCart, [addonToRemove]);
            }
            endFetching();
        } catch (e) {
            endFetching(e);
        }
    }, [startFetching, cartAddonList, endFetching, addonIdList, offerId, setCart, setNumberOfferSelected]);

    const clearAddons = useCallback(async () => {
        try {
            startFetching();
            const addonList = cartAddonList.filter((addon) => addonIdList.includes(addon.id));

            if (addonList.length) {
                let newCart: ICart | undefined = undefined;
                for (const addon of addonList) {
                    newCart = await deleteAddon(offerId, addon.id);
                }
                const newAddonIdList = mapNewCartAddonIdList(newCart);
                setAddonIdList(newAddonIdList);
                setCart(newCart);

                if (newCart) {
                    setNumberOfferSelected(getCartItemsNumber(newCart));
                    sendRemoveAddonEvent(newCart, addonList);
                }
            }

            endFetching();
        } catch (e) {
            endFetching(e);
        }
    }, [startFetching, cartAddonList, setCart, endFetching, addonIdList, offerId, setNumberOfferSelected]);

    return { wifiMeshOfferAddon, addAddon, removeAddon, clearAddons, counter, isLoading };
};
